import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'

import { NextSeo } from 'next-seo'

import { Box, Grid } from '@material-ui/core'
import { Pagination } from '@material-ui/lab'

import BlogPhotoCard from '~/components/cards/BlogPhotoCard'
import AppLayout from '~/components/layouts/AppLayout'
import SubscribeSection from '~/components/sections/SubscribeSection'
import Flexbox from '~/components/shared/Flexbox'
import HeroSection from '~/components/shared/HeroSection'
import LoaderBox from '~/components/shared/LoaderBox'
import Section from '~/components/shared/Section'
import StickyContainer from '~/components/shared/StickyContainer'
import Text, { Title } from '~/components/shared/Text'
import Desktop from '~/containers/Desktop'
import Mobile from '~/containers/Mobile'
import { ReCaptchaProvider } from '~/containers/ReCaptchaProvider'
import BlogHeader from '~/views/Blog/BlogHeader'
import BlogSubscribe from '~/views/Blog/BlogSubscribe'

import { useMobile } from '~/hooks/responsive'
import { useGetSearchArticles } from '~/services/Query/news-reviews/news-review.query'
import { canStartRequestWithQuery } from '~/utils/functions'
import Paths from '~/utils/paths'

import Boat from '~/assets/icons/boat/categories/boat.svg?sprite'

import { formatMessage, getEntityByBrand } from '~/brands'
import { IS_BROWSER } from '~/constants'

import bgImageBoat from '~/../public/static/images/blog-hero-category.boat.jpg'
import bgImageRv from '~/../public/static/images/blog-hero-category.rv.jpg'

const bgImage = getEntityByBrand<string>({ rv: bgImageRv, boat: bgImageBoat })

const PAGE_SIZE = 12

export const BlogSearch = () => {
  const router = useRouter()
  const isMobile = useMobile()

  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(+router.query.page || 1)
  }, [router.query.page])

  const start = canStartRequestWithQuery(router.query)

  const { data: featuredArticles } = useGetSearchArticles({
    params: {
      byCategory: router.query?.category as string,
      limit: 3,
      featured: true
    }
  })

  const { data, isLoading, error } = useGetSearchArticles({
    params: {
      byKeyword: router.query?.keywords as string,
      search: router.query?.search as string,
      byCategory: router.query?.category as string,
      featured: router.query?.featuredOnly === 'true',
      page: +router.query.page || 1,
      limit: PAGE_SIZE
    }
  })

  const firstLoading = (!data.articles?.length && isLoading && !error) || !start

  const handleChangePage = (_e, newPage) => {
    if (IS_BROWSER) {
      window.scrollTo({ top: 0 })
    }
    router.push({ query: { ...router.query, page: newPage } })
  }

  const sidebar = (
    <>
      <ReCaptchaProvider>
        <BlogSubscribe />
      </ReCaptchaProvider>

      {!!featuredArticles?.articles?.length && (
        <Box
          width="100%"
          bgcolor="bg.primary.main"
          p={2}
          mt={4}
          mr={0}
          position="relative"
          borderRadius="4px"
        >
          <Box position="absolute" bottom="10%" right={0}>
            <Boat />
          </Box>
          <Text fontWeight={600} fontSize={20}>
            Featured
          </Text>
          <ul>
            {featuredArticles?.articles?.map((item) => {
              return (
                <li key={item.id} style={{ paddingBottom: '10px' }}>
                  <Link
                    href={Paths.newsReviews.buildArticle(
                      item.id,
                      item.content_name
                    )}
                  >
                    <Text
                      fontWeight={500}
                      fontSize={18}
                      style={{ cursor: 'pointer' }}
                    >
                      {item.content_name}
                    </Text>
                  </Link>
                </li>
              )
            })}
          </ul>
        </Box>
      )}
    </>
  )

  const pagination = (
    <Flexbox mx="auto" justifyContent="center" mt={4}>
      <Pagination
        count={Math.ceil(data.pagy?.count / PAGE_SIZE)}
        color="primary"
        shape="rounded"
        size={isMobile ? 'medium' : 'large'}
        onChange={handleChangePage}
        page={page}
      />
    </Flexbox>
  )

  return (
    <AppLayout
      disableGutters
      containerProps={{ disableGutters: true, maxWidth: false }}
    >
      <NextSeo
        title="Search"
        description={formatMessage('reviewsNewsPage.meta.description')}
        openGraph={{
          title: 'Search',
          description: formatMessage('reviewsNewsPage.meta.description')
        }}
      />

      <BlogHeader />

      <HeroSection src={bgImage} height="250px" zIndex={0}>
        <Title m="auto" variant="h1" style={{ color: 'white' }}>
          Search
        </Title>
      </HeroSection>
      <Section bgcolor="common.white" title="Search Results">
        <Grid container spacing={4}>
          <Grid item xs={12} md={9} lg={9}>
            <Grid container spacing={4}>
              <LoaderBox loading={firstLoading} length={data.articles.length}>
                {data.articles.map((item) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                    <BlogPhotoCard data={item} loading={isLoading} />
                  </Grid>
                ))}
              </LoaderBox>
            </Grid>
          </Grid>
          {data.pagy?.count > PAGE_SIZE &&
            !!data.articles?.length &&
            isMobile &&
            pagination}
          <Grid item md={3} lg={3}>
            <Desktop>
              <StickyContainer>{sidebar}</StickyContainer>
            </Desktop>
            <Mobile>{sidebar}</Mobile>
          </Grid>
        </Grid>
        {data.pagy?.count > PAGE_SIZE &&
          !!data.articles?.length &&
          !isMobile &&
          pagination}
      </Section>

      <ReCaptchaProvider>
        <SubscribeSection />
      </ReCaptchaProvider>
    </AppLayout>
  )
}

export default BlogSearch
